<template>
  <div class="login">
    <div v-show="loading" class="loading-ring"></div>
    <div v-show="!loading">
      <form v-show="!googleEnabled" @submit.prevent="login">
        <button type="submit" style="display: none"></button>
        <el-input
          class="user"
          :placeholder="$t('login.user')"
          v-model="form.username"
          @keyup.enter="login"
        />
        <el-input
          class="user"
          :placeholder="$t('login.password')"
          type="password"
          v-model="form.password"
          @keyup.enter="login"
        />
        <el-button icon="el-icon-arrow-right" circle v-on:click="login"></el-button>
      </form>
      <div class="google" v-show="googleEnabled">
        <div ref="googleButton"></div>
      </div>
    </div>
  </div>
</template>

<script>
import { signIn, signInWithGoogle, signOut } from '../services/AuthService';
import { googleClientId, loadGoogleIdentity } from '../utils/GoogleIdentity';

export default {
  name: 'snowflake-login',
  data() {
    return {
      loading: false,
      // Configuring a client id replaces the email/password form with the Google button.
      googleEnabled: Boolean(googleClientId()),
      form: {
        username: null,
        password: null,
      },
    };
  },
  methods: {
    persistSession({ role, roles, token }) {
      const userId = JSON.parse(atob(token.split('.')[1])).user_id;

      this.$storage.removeAll();

      if (roles) {
        this.$storage.add(this.$storage.keys.auth.roles, btoa(JSON.stringify(roles)));
      }

      if (role) {
        this.$storage.add(this.$storage.keys.auth.role, btoa(role));
      }

      this.$storage.add(this.$storage.keys.auth.token, token);
      this.$storage.add(this.$storage.keys.auth.user, userId);
    },
    showError(key) {
      this.$message({
        message: this.$t(key),
        center: true,
        showClose: true,
      });
    },
    async login() {
      try {
        this.loading = true;
        this.persistSession(await signIn(this.form.username, this.form.password));

        this.loading = false;
        this.$router.push({ path: '/' });
      } catch (e) {
        this.loading = false;
        this.showError('login.error.invalidInformation');
      }
    },
    // Google Identity Services hands back the OpenID Connect ID token as `response.credential`.
    async onGoogleCredential(response) {
      try {
        this.loading = true;
        this.persistSession(await signInWithGoogle(response.credential));

        this.loading = false;
        this.$router.push({ path: '/' });
      } catch (e) {
        this.loading = false;
        this.showError('login.error.googleSignInFailed');
      }
    },
  },
  async mounted() {
    if (!this.googleEnabled) return;

    try {
      const googleIdentity = await loadGoogleIdentity();

      googleIdentity.initialize({ client_id: googleClientId(), callback: this.onGoogleCredential });
      googleIdentity.renderButton(this.$refs.googleButton, {
        theme: 'outline',
        size: 'large',
        width: 300,
      });
    } catch (e) {
      // Fall back to email/password rather than leaving no way in.
      this.googleEnabled = false;
    }
  },
  created() {
    if (this.$router.currentRoute.name === 'logout') {
      signOut();
      this.$storage.removeAll();
    }
  },
};
</script>

<style lang="scss" scoped>
.login {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 300px;

  .el-input {
    margin-bottom: 15px;
  }

  .el-button {
    float: right;
  }

  .google {
    display: flex;
    justify-content: center;
  }

  .loading-ring {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    display: inline-block;
    width: 64px;
    height: 64px;
  }

  .loading-ring:after {
    content: " ";
    display: block;
    width: 46px;
    height: 46px;
    margin: 1px;
    border-radius: 50%;
    border: 5px solid #CB264E;
    border-color: #CB264E transparent #CB264E transparent;
    animation: loading-ring 1.2s linear infinite;
  }

  @keyframes loading-ring {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
}
</style>
